import { expressions, regexValidators } from "../../survey/validators";
import { translate } from "../../lib/intl";

// Most fields are optional; only fields with explicit `isRequired: true` are required.
const required = false;

const choice = (value, en, rw = en) => ({ value, text: { en, rw } });

const cropChoices = [
  choice("avocado", "Avocado"),
  choice("banana", "Banana"),
  choice("beans", "Beans"),
  choice("carrots", "Carrots"),
  choice("cassava", "Cassava"),
  choice("chili", "Chili"),
  choice("coffee", "Coffee"),
  choice("fish", "Fish"),
  choice("horticulture", "Horticulture"),
  choice("maize", "Maize"),
  choice("onion", "Onion"),
  choice("orange_sweet_potato", "Orange sweet potato"),
  choice("passion_fruit", "Passion fruit"),
  choice("potato", "Potato"),
  choice("pyrethrum", "Pyrethrum"),
  choice("rice", "Rice"),
  choice("soybean", "Soybean"),
  choice("tea", "Tea"),
  choice("tomato", "Tomato"),
  choice("wheat", "Wheat"),
];

const generalInformation = (clientUrlForCatalog) => ({
  name: "page1",
  title: {
    en: "Cooperative general information",
    rw: "Amakuru rusange y'ishyirahamwe",
  },
  elements: [
    {
      type: "expression",
      name: "cooperative_name",
      title: {
        en: "Name of the cooperative",
        rw: "Izina ry'ishyirahamwe",
      },
      expression: "{cooperative_name}",
      includeIntoResult: false,
    },
    {
      name: "province",
      type: "dropdown",
      title: {
        en: "Province where the cooperative is located",
        rw: "Intara ishyirahamwe riherereyemo",
      },

      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/provinces/`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    { type: "text", name: "Idprovince", visible: false },
    {
      name: "district",
      type: "dropdown",
      title: {
        en: "District where the cooperative is located",
        rw: "Akarere ishyirahamwe riherereyemo",
      },
      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/districts/?province_id={Idprovince}`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    { type: "text", name: "Iddistrict", visible: false },
    {
      name: "sector",
      type: "dropdown",
      title: {
        en: "Sector where the cooperative is located",
        rw: "Umurenge ishyirahamwe riherereyemo",
      },
      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/sectors/?district_id={Iddistrict}`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    { type: "text", name: "Idsector", visible: false },
    {
      name: "cell",
      type: "dropdown",
      title: {
        en: "Cell where the cooperative is located",
        rw: "Akagari ishyirahamwe riherereyemo",
      },
      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/cells/?sector_id={Idsector}`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    { type: "text", name: "Idcell", visible: false },
    {
      name: "village",
      type: "dropdown",
      title: {
        en: "Village where the cooperative is located",
        rw: "Umudugudu ishyirahamwe riherereyemo",
      },
      renderAs: "select2",
      choicesByUrl: {
        url: `${clientUrlForCatalog}/api/tenant-data/villages/?cell_id={Idcell}`,
        path: "results",
        titleName: "value",
        valueName: "value",
        attachOriginalItems: true,
      },
    },
    {
      type: "text",
      name: "president_name",
      title: {
        en: "Name of the president of the cooperative",
        rw: "Izina rya perezida w'ishyirahamwe",
      },
    },
    {
      type: "phone",
      name: "president_phone",
      title: {
        en: "Phone number of the president of the cooperative",
        rw: "Nimero ya telefone ya perezida w'ishyirahamwe",
      },
      validators: [
        {
          type: "regex",
          regex: "^(\\+?250)?7[0-9]{8}$",
          text: {
            en: "Phone number must be 9 digits starting with 7 (e.g. 78XXXXXXX)",
            rw: "Nimero ya telefone igomba kuba igizwe n'imibare 9 itangirwa na 7 (urugero: 78XXXXXXX)",
          },
        },
      ],
    },
    {
      type: "text",
      name: "email",
      inputType: "email",
      title: {
        en: "E-mail address of the cooperative",
        rw: "Aderesi ya imeri y'ishyirahamwe",
      },
      validators: [
        {
          type: "email",
          text: {
            en: "Please enter a valid e-mail address",
            rw: "Nyamuneka andika aderesi ya imeri yemewe",
          },
        },
      ],
    },
    {
      type: "text",
      name: "members",
      inputType: "number",
      title: {
        en: "Total number of members of the cooperative",
        rw: "Umubare wose w'abanyamuryango b'ishyirahamwe",
      },
      validators: [
        regexValidators.integer(),
        {
          type: "numeric",
          minValue: 1,
          text: {
            en: "Total number of members must be at least 1",
            rw: "Umubare wose w'abanyamuryango ugomba kuba nibura 1",
          },
        },
      ],
    },
    {
      type: "text",
      name: "members_men",
      inputType: "number",
      min: 0,
      title: { en: "Of which men", rw: "Ku bo bagabo" },
      validators: [
        regexValidators.integer(),
        expressions.non_negative_number(),
        expressions.max_members("members_men"),
        {
          type: "expression",
          expression:
            "{members} empty or {members_men} empty or {members_women} empty or ({members_men} + {members_women} = {members})",
          text: {
            en: "The sum of men and women must equal total members",
            rw: "Igiteranyo cy'abagabo n'abagore kigomba kungana n'umubare wose w'abanyamuryango",
          },
        },
      ],
    },
    {
      type: "text",
      name: "members_women",
      inputType: "number",
      min: 0,
      title: { en: "Of which women", rw: "Ku bo bagore" },
      validators: [
        regexValidators.integer(),
        expressions.non_negative_number(),
        expressions.max_members("members_women"),
        {
          type: "expression",
          expression:
            "{members} empty or {members_men} empty or {members_women} empty or ({members_men} + {members_women} = {members})",
          text: {
            en: "The sum of men and women must equal total members",
            rw: "Igiteranyo cy'abagabo n'abagore kigomba kungana n'umubare wose w'abanyamuryango",
          },
        },
      ],
    },
    {
      type: "text",
      name: "members_youth",
      inputType: "number",
      min: 0,
      title: {
        en: "Number of youth members (<35)",
        rw: "Umubare w'urubyiruko (<35)",
      },
      validators: [
        regexValidators.integer(),
        expressions.non_negative_number(),
        expressions.max_members("members_youth"),
      ],
    },
    {
      type: "text",
      name: "members_disability",
      inputType: "number",
      min: 0,
      title: {
        en: "Number of people with a disability",
        rw: "Umubare w'abafite ubumuga",
      },
      validators: [
        regexValidators.integer(),
        expressions.non_negative_number(),
        expressions.max_members("members_disability"),
      ],
    },
    {
      type: "comment",
      name: "description",
      title: {
        en: "Observations and comments",
        rw: "Ibitekerezo n'ibisobanuro",
      },
    },
  ],
});

const productionValueChain = () => ({
  name: "page2",
  title: {
    en: "Production & Value Chain",
    rw: "Umusaruro n'urwego rw'agaciro",
  },
  elements: [
    {
      type: "checkbox",
      name: "seasons",
      title: {
        en: "During which seasons do cooperative members cultivate crops?",
        rw: "Mu bihembwe ihe abanyamuryango b'ishyirahamwe bahinga?",
      },
      choices: [
        choice("season_a", "A (September - January)"),
        choice("season_b", "B (February - June)"),
        choice("season_c", "C (June - September)"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "start_season_a",
      visibleIf: "{seasons} contains 'season_a'",
      requiredIf: "{seasons} contains 'season_a'",
      requiredErrorText: {
        en: "Please select the start of planting for Season A",
        rw: "Nyamuneka hitamo igihe cyo gutera mu gihembwe A",
      },
      title: {
        en: "What is the typical start of the planting period for Season A?",
        rw: "Ni ryari ubusanzwe bwo gutangira gutera mu gihembwe A?",
      },
      choices: [
        choice("late_august", "Late August"),
        choice("early_september", "Early September"),
        choice("late_september", "Late September"),
        choice("early_october", "Early October"),
        choice("late_october", "Late October"),
        choice("early_november", "Early November"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "crops_season_a",
      visibleIf: "{seasons} contains 'season_a'",
      requiredIf: "{seasons} contains 'season_a'",
      requiredErrorText: {
        en: "Please select at least one crop for Season A",
        rw: "Nyamuneka hitamo nibura igihingwa kimwe mu gihembwe A",
      },
      title: {
        en: "Which crops are cultivated by cooperative members during Season A?",
        rw: "Ni ibihe bihingwa abanyamuryango bahinga mu gihembwe A?",
      },
      choices: cropChoices,
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "harvest_season_a",
      visibleIf: "{seasons} contains 'season_a'",
      requiredIf: "{seasons} contains 'season_a'",
      requiredErrorText: {
        en: "Please select the start of harvest for Season A",
        rw: "Nyamuneka hitamo igihe cyo gusarura mu gihembwe A",
      },
      title: {
        en: "What is the typical start of the harvest of Season A?",
        rw: "Ni ryari ubusanzwe bwo gutangira gusarura mu gihembwe A?",
      },
      choices: [
        choice("december", "December"),
        choice("january", "January"),
        choice("february", "February"),
        choice("march", "March"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "start_season_b",
      visibleIf: "{seasons} contains 'season_b'",
      requiredIf: "{seasons} contains 'season_b'",
      requiredErrorText: {
        en: "Please select the start of planting for Season B",
        rw: "Nyamuneka hitamo igihe cyo gutera mu gihembwe B",
      },
      title: {
        en: "What is the typical start of the planting period for Season B?",
        rw: "Ni ryari ubusanzwe bwo gutangira gutera mu gihembwe B?",
      },
      choices: [
        choice("late_january", "Late January"),
        choice("early_february", "Early February"),
        choice("late_february", "Late February"),
        choice("early_march", "Early March"),
        choice("late_march", "Late March"),
        choice("early_april", "Early April"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "crops_season_b",
      visibleIf: "{seasons} contains 'season_b'",
      requiredIf: "{seasons} contains 'season_b'",
      requiredErrorText: {
        en: "Please select at least one crop for Season B",
        rw: "Nyamuneka hitamo nibura igihingwa kimwe mu gihembwe B",
      },
      title: {
        en: "Which crops are cultivated by cooperative members during Season B?",
        rw: "Ni ibihe bihingwa abanyamuryango bahinga mu gihembwe B?",
      },
      choices: cropChoices,
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "harvest_season_b",
      visibleIf: "{seasons} contains 'season_b'",
      requiredIf: "{seasons} contains 'season_b'",
      requiredErrorText: {
        en: "Please select the start of harvest for Season B",
        rw: "Nyamuneka hitamo igihe cyo gusarura mu gihembwe B?",
      },
      title: {
        en: "What is the typical start of the harvest of Season B?",
        rw: "Ni ryari ubusanzwe bwo gutangira gusarura mu gihembwe B?",
      },
      choices: [
        choice("may", "May"),
        choice("june", "June"),
        choice("july", "July"),
        choice("august", "August"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "start_season_c",
      visibleIf: "{seasons} contains 'season_c'",
      requiredIf: "{seasons} contains 'season_c'",
      requiredErrorText: {
        en: "Please select the start of planting for Season C",
        rw: "Nyamuneka hitamo igihe cyo gutera mu gihembwe C",
      },
      title: {
        en: "What is the typical start of the planting period for Season C?",
        rw: "Ni ryari ubusanzwe bwo gutangira gutera mu gihembwe C?",
      },
      choices: [
        choice("late_may", "Late May"),
        choice("early_june", "Early June"),
        choice("late_june", "Late June"),
        choice("early_july", "Early July"),
        choice("late_july", "Late July"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "crops_season_c",
      visibleIf: "{seasons} contains 'season_c'",
      requiredIf: "{seasons} contains 'season_c'",
      requiredErrorText: {
        en: "Please select at least one crop for Season C",
        rw: "Nyamuneka hitamo nibura igihingwa kimwe mu gihembwe C",
      },
      title: {
        en: "Which crops are cultivated by cooperative members during Season C?",
        rw: "Ni ibihe bihingwa abanyamuryango bahinga mu gihembwe C?",
      },
      choices: cropChoices,
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "harvest_season_c",
      visibleIf: "{seasons} contains 'season_c'",
      requiredIf: "{seasons} contains 'season_c'",
      requiredErrorText: {
        en: "Please select the start of harvest for Season C",
        rw: "Nyamuneka hitamo igihe cyo gusarura mu gihembwe C",
      },
      title: {
        en: "What is the typical start of the harvest of Season C?",
        rw: "Ni ryari ubusanzwe bwo gutangira gusarura mu gihembwe C?",
      },
      choices: [
        choice("august", "August"),
        choice("september", "September"),
        choice("october", "October"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "main_crop",
      visibleIf:
        "{crops_season_a} notempty or {crops_season_b} notempty or {crops_season_c} notempty",
      title: {
        en: "What is the main crop cultivated by cooperative members?",
        rw: "Ni iki gihingwa nyamukuru cy'ishyirahamwe?",
      },
      choices: cropChoices,
      choicesVisibleIf:
        "{crops_season_a} contains {item} or {crops_season_b} contains {item} or {crops_season_c} contains {item}",
    },
    {
      type: "matrixdropdown",
      name: "total_produce_last_season",
      visibleIf:
        "{crops_season_a} notempty or {crops_season_b} notempty or {crops_season_c} notempty or {main_crop} notempty",
      title: {
        en: "What was the total volume of produce delivered by cooperative members during the last season?",
        rw: "Ni iyihe ngingo y'umusaruro watanzwe n'abanyamuryango mu gihembwe gishize?",
      },
      columns: [
        {
          name: "tonnes_last_season",
          title: {
            en: "Tonnes last season",
            rw: "Tone mu gihembwe gishize",
          },
          cellType: "text",
          inputType: "number",
          isRequired: required,
          validators: [expressions.non_negative_number()],
        },
      ],
      rows: cropChoices,
      rowsVisibleIf:
        "{crops_season_a} contains {item} or {crops_season_b} contains {item} or {crops_season_c} contains {item} or {main_crop} = {item}",
    },
    {
      type: "text",
      name: "land_of_cooperative",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "How many hectares of land are owned by the cooperative?",
        rw: "Ni hegitari zingahe z'ubutaka ishyirahamwe rifite?",
      },
    },
    {
      type: "text",
      name: "land_owners",
      inputType: "number",
      min: 0,
      title: {
        en: "How many cooperative members own agricultural land?",
        rw: "Ni abanyamuryango bangahe bafite ubutaka bw'ubuhinzi?",
      },
      validators: [
        regexValidators.integer(),
        expressions.non_negative_number(),
        expressions.max_members("land_owners"),
      ],
    },
    {
      type: "text",
      name: "land_size_of_members",
      inputType: "number",
      validators: [expressions.non_negative_number()],
      title: {
        en: "What is the estimated total cultivated land of cooperative members?",
        rw: "Ni hegitari zingahe z'ubutaka bw'ubuhinzi bw'abanyamuryango bose?",
      },
    },
    {
      type: "text",
      name: "cost_of_production",
      inputType: "number",
      validators: [
        regexValidators.integer(),
        {
          type: "numeric",
          minValue: 1,
          text: {
            en: "Please enter a whole number greater than 0",
            rw: "Andika umubare wuzuye urenze 0",
          },
        },
      ],
      title: {
        en: "What is the estimated cost of production per hectare for the main crop (in RWF)?",
        rw: "Ni amafaranga angahe y'igishoro ku hegitari ku gihingwa nyamukuru (muri RWF)?",
      },
    },
    {
      type: "text",
      name: "revenue_per_hectare",
      inputType: "number",
      validators: [
        regexValidators.integer(),
        {
          type: "numeric",
          minValue: 1,
          text: {
            en: "Please enter a whole number greater than 0",
            rw: "Andika umubare wuzuye urenze 0",
          },
        },
      ],
      title: {
        en: "What is the estimated revenue per hectare for the main crop (in RWF)?",
        rw: "Ni amafaranga angahe yinjiye ku hegitari ku gihingwa nyamukuru (muri RWF)?",
      },
    },
  ],
});

const marketAccess = () => ({
  name: "page3",
  title: { en: "Market Access", rw: "Kugera ku isoko" },
  elements: [
    {
      type: "dropdown",
      name: "produce_collection_by_cooperative",
      title: {
        en: "Does the cooperative collect members' produce and sell it to buyers on their behalf?",
        rw: "Ese ishyirahamwe rikusanya umusaruro w'abanyamuryango rigatugisha abaguzi ku bwabo?",
      },
      choices: [
        choice("all_all", "Yes, all produce for all members"),
        choice("some_all", "Yes, some produce for all members"),
        choice("all_some", "Yes, all produce for some members"),
        choice("some_some", "Yes, some produce for some members"),
        choice("no", "No"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "payments_produce",
      title: {
        en: "How are payments for produce typically made?",
        rw: "Ubusanzwe kwishyurwa umusaruro bikorwa gute?",
      },
      choices: [
        choice(
          "cooperative_pays",
          "The cooperative receives payment from buyers and pays members",
        ),
        choice("buyers_pay_members", "Buyers pay members directly"),
        choice("both", "Both arrangements are used"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "text",
      name: "largest_buyer_percentage",
      inputType: "number",
      min: 0,
      max: 100,
      validators: [
        {
          type: "numeric",
          minValue: 0,
          maxValue: 100,
          text: {
            en: "Please enter a percentage between 0 and 100",
            rw: "Andika ijanisha riri hagati ya 0 na 100",
          },
        },
      ],
      title: {
        en: "What percentage of sales is made to the largest buyer?",
        rw: "Ni irihe percent by'agurishwa ku muguzi mukuru?",
      },
    },
    {
      type: "dropdown",
      name: "buyer_agreements",
      title: {
        en: "What type of agreements does the cooperative have with its buyers?",
        rw: "Ni ubuhe bwoko bw'amasezerano ishyirahamwe rifite n'abaguzi bayo?",
      },
      choices: [
        choice("no_formal", "No formal agreements"),
        choice("verbal_only", "Verbal agreements only"),
        choice("written_some", "Written agreements with some buyers"),
        choice("written_most", "Written agreements with most buyers"),
      ],
    },
    {
      type: "checkbox",
      name: "buyer_types",
      title: {
        en: "What types of buyers does the cooperative sell to?",
        rw: "Ni ubuhe bwoko bw'abaguzi ishyirahamwe rigurishaho?",
      },
      choices: [
        choice("exporters", "Exporters"),
        choice("agro_processing", "Agro-processing companies"),
        choice("inputs_suppliers", "Inputs suppliers"),
        choice("traders_wholesalers", "Traders, wholesalers"),
        choice("big_farmers", "Big farmers"),
        choice("hotels_restaurants", "Hotels, restaurants, caterers"),
        choice("retailers", "Retailers, local market buyers"),
        choice(
          "institutional",
          "Institutional buyers (schools, hospitals, government)",
        ),
        choice(
          "individual_consumers",
          "Individual consumers, neighbours, friends",
        ),
        choice(
          "refugee_humanitarian",
          "Refugee camps, humanitarian organizations",
        ),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "dropdown",
      name: "unable_to_sell",
      title: {
        en: "How often is the cooperative unable to sell all produce produced by its members?",
        rw: "Kukihe gihe ishyirahamwe ritagurisha umusaruro wose w'abanyamuryango?",
      },
      choices: [
        choice("very_often", "Very often"),
        choice("often", "Often"),
        choice("sometimes", "Sometimes"),
        choice("rarely", "Rarely"),
        choice("never", "Never"),
      ],
    },
    {
      type: "text",
      name: "rejection_percentage",
      inputType: "number",
      min: 0,
      max: 100,
      validators: [
        {
          type: "numeric",
          minValue: 0,
          maxValue: 100,
          text: {
            en: "Please enter a percentage between 0 and 100",
            rw: "Andika ijanisha riri hagati ya 0 na 100",
          },
        },
      ],
      title: {
        en: "What was the approximate percentage of rejection of produce during the last season?",
        rw: "Ni irihe percent ry'umusaruro wanzwe mu gihembwe gishize?",
      },
    },
    {
      type: "dropdown",
      name: "reason_for_rejection",
      visibleIf: "{rejection_percentage} > 0",
      title: {
        en: "What is the primary reason for produce rejection?",
        rw: "Ni iki cy'ingenzi cyatumye umusaruro unzwe?",
      },
      choices: [
        choice("poor_quality", "Poor quality"),
        choice("moisture_content", "Moisture content"),
        choice("pest_disease", "Pest or disease damage"),
        choice("buyer_specs", "Failure to meet buyer specifications"),
        choice("oversupply", "Oversupply"),
        choice("storage_issues", "Storage issues"),
        choice("transport_damage", "Transport damage"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
  ],
});

const operationalMaturity = () => ({
  name: "page4",
  title: { en: "Operational Maturity", rw: "Ubushobozi bwo gukora" },
  elements: [
    {
      type: "text",
      name: "permanent_staff",
      inputType: "number",
      validators: [regexValidators.non_negative_integer()],
      title: {
        en: "What is the number of permanent staff members?",
        rw: "Ni abakozi bangahe b'igihe kirekire?",
      },
    },
    {
      type: "text",
      name: "year_of_establishment",
      maxLength: 4,
      placeHolder: {
        en: "YYYY (e.g. 2018)",
        rw: "YYYY (urugero: 2018)",
      },
      title: {
        en: "What is the year of establishment of the cooperative?",
        rw: "Ni mu wuhe mwaka ishyirahamwe ryashinzwe?",
      },
      description: {
        en: "Enter the 4-digit calendar year (e.g. 2018)",
        rw: "Andika umwaka w'imibare 4 (urugero: 2018)",
      },
      validators: [
        {
          type: "regex",
          regex: "^[0-9]{4}$",
          text: {
            en: "Year of establishment must be a 4-digit year (e.g. 2018)",
            rw: "Umwaka wo gushinga ugomba kuba ugizwe n'imibare 4 (urugero: 2018)",
          },
        },
        {
          type: "expression",
          expression: `{year_of_establishment} empty or ({year_of_establishment} >= 1800 and {year_of_establishment} <= ${new Date().getFullYear()})`,
          text: {
            en: `Year of establishment must be between 1800 and the current year (${new Date().getFullYear()})`,
            rw: `Umwaka wo gushinga ugomba kuba hagati ya 1800 n'umwaka uriho (${new Date().getFullYear()})`,
          },
        },
      ],
    },
    {
      type: "expression",
      name: "years_active",
      title: {
        en: "Years active (calculated automatically)",
        rw: "Imyaka ishyirahamwe rimaze rikora (byikora)",
      },
      expression: "calcYearsActive({year_of_establishment})",
      includeIntoResult: true,
    },
    {
      type: "dropdown",
      name: "sales_records",
      title: {
        en: "What transaction records does the cooperative maintain for sales to buyers?",
        rw: "Ni ayahe makuru y'ubucuruzi ishyirahamwe ritunga ku kugurisha abaguzi?",
      },
      choices: [
        choice("no_records", "No records"),
        choice("sales_only", "Sales records only"),
        choice("production_only", "Production records only"),
        choice("both", "Both production and sales records"),
        choice(
          "both_linked",
          "Both production and sales records linked to individual members",
        ),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "checkbox",
      name: "data_recording",
      hasNone: true,
      noneText: { en: "None", rw: "Nta na kimwe" },
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
      title: {
        en: "Which data does the cooperative record for individual members?",
        rw: "Ni ayahe makuru ishyirahamwe ritunga ku banyamuryango ku giti cyabo?",
      },
      choices: [
        choice("production_data", "Production data"),
        choice("sales_data", "Sales data"),
        choice("land_size", "Land size"),
        choice("productivity", "Productivity"),
      ],
      validators: [
        {
          type: "expression",
          text: {
            en: "'None' cannot be selected together with other options",
            rw: "'Nta na kimwe' ntigishobora gutoranywa hamwe n'ibindi",
          },
          expression:
            "{data_recording} empty or {data_recording} notcontains 'none' or count({data_recording}) = 1",
        },
      ],
    },
    {
      type: "dropdown",
      name: "record_keeping",
      enableIf: "{sales_records} != 'no_records'",
      validators: [
        {
          type: "expression",
          text: {
            en: "If transaction records for sales is 'No records', record keeping must be 'No records'",
            rw: "Iyo nta nyandiko z'ubucuruzi zihari, uburyo bwo kubika bugomba kuba 'Nta nyandiko'",
          },
          expression:
            "{sales_records} != 'no_records' or {record_keeping} empty or {record_keeping} = 'no_records'",
        },
      ],
      title: {
        en: "How are these records maintained?",
        rw: "Aya makuru atungwa ate?",
      },
      choices: [
        choice("no_records", "No records"),
        choice("basic_handwritten", "Basic handwritten"),
        choice("organized_paper", "Organized paper records"),
        choice("sheets_excel", "Sheets / Excel"),
        choice("dedicated_software", "Dedicated software"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "radiogroup",
      name: "has_bank_account",
      title: {
        en: "Does the cooperative have a bank or SACCO account in its own name?",
        rw: "Ese ishyirahamwe rifite konti ya banki cyangwa SACCO mu izina ryaryo?",
      },
      choices: [choice("no", "No", "Oya"), choice("yes", "Yes", "Yego")],
    },
    {
      type: "text",
      name: "bank_name",
      visibleIf: "{has_bank_account} = 'yes'",
      requiredIf: "{has_bank_account} = 'yes'",
      clearIfInvisible: "onHidden",
      requiredErrorText: {
        en: "Please enter the name of the bank or SACCO",
        rw: "Nyamuneka andika izina rya banki cyangwa SACCO",
      },
      title: {
        en: "Name of the bank or SACCO",
        rw: "Izina rya banki cyangwa SACCO",
      },
    },
    {
      type: "text",
      name: "bank_account_name",
      visibleIf: "{has_bank_account} = 'yes'",
      requiredIf: "{has_bank_account} = 'yes'",
      clearIfInvisible: "onHidden",
      requiredErrorText: {
        en: "Please enter the account name",
        rw: "Nyamuneka andika izina riri kuri konti",
      },
      title: {
        en: "Account name",
        rw: "Izina riri kuri konti",
      },
    },
    {
      type: "text",
      name: "bank_account_number",
      visibleIf: "{has_bank_account} = 'yes'",
      requiredIf: "{has_bank_account} = 'yes'",
      clearIfInvisible: "onHidden",
      requiredErrorText: {
        en: "Please enter the account number",
        rw: "Nyamuneka andika nimero ya konti",
      },
      title: {
        en: "Account number",
        rw: "Nimero ya konti",
      },
      validators: [
        {
          type: "regex",
          regex: "^(?=.*\\d)[0-9-]+$",
          text: {
            en: "Account number must contain only digits and dashes",
            rw: "Nimero ya konti igomba kuba igizwe gusa n'imibare n'udusharu (-)",
          },
        },
      ],
    },
    {
      type: "dropdown",
      name: "data_verification",
      enableIf: "{sales_records} != 'no_records'",
      validators: [
        {
          type: "expression",
          text: {
            en: "If transaction records for sales is 'No records', record verification must be 'No records available'",
            rw: "Iyo nta nyandiko z'ubucuruzi zihari, kugenzura bigomba kuba 'Nta nyandiko zihari'",
          },
          expression:
            "{sales_records} != 'no_records' or {data_verification} empty or {data_verification} = 'no_records'",
        },
      ],
      title: {
        en: "Did a MoneyPhone loan officer verify the production and sales records?",
        rw: "Ese umukozi w'inguzanyo wa MoneyPhone yemeje inyandiko z'umusaruro n'ubucuruzi?",
      },
      choices: [
        choice("no_records", "No records available"),
        choice("production_verified", "Production records verified"),
        choice("sales_verified", "Sales records verified"),
        choice("both_verified", "Both production and sales records verified"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "radiogroup",
      name: "member_selection_loan",
      title: {
        en: "How does the cooperative select members who can apply for a loan?",
        rw: "Ni gute ishyirahamwe rihitamo abanyamuryango bashobora gusaba inguzanyo?",
      },
      choices: [
        choice(
          "no_selection",
          "No selection, all members can apply",
          "Nta gutoranya, abanyamuryango bose barashobora gusaba",
        ),
        choice(
          "president_decides",
          "The president decides",
          "Perezida ni we uhitamo",
        ),
        choice(
          "committee_criteria",
          "A committee decides using agreed criteria",
          "Komite ni yo ihitamo igendeye ku bipimo byumvikanyweho",
        ),
        choice(
          "committee_criteria_repayment",
          "A committee decides using agreed criteria and members’ past repayment",
          "Komite ni yo ihitamo igendeye ku bipimo byumvikanyweho n'imyitwarire yo kwishyura y'abanyamuryango",
        ),
      ],
    },
  ],
});

const financialPerformance = () => ({
  name: "page5",
  title: { en: "Financial Performance", rw: "Imikorere y'imari" },
  elements: [
    {
      type: "checkbox",
      name: "sources_of_income",
      title: {
        en: "What are the cooperative's main sources of income?",
        rw: "Ni ayahe maturutse y'inyungu y'ishyirahamwe?",
      },
      choices: [
        choice("crop_aggregation", "Crop aggregation / trading (Agrodealer)"),
        choice("seed_multiplication", "Seed multiplication"),
        choice("input_sales", "Input sales"),
        choice("agro_processing", "Agro-processing"),
        choice("transport_services", "Transport services"),
        choice("equipment_rental", "Equipment rental"),
        choice("hospitality_services", "Hospitality services"),
        choice("membership_fees", "Membership fees"),
        choice("commissions", "Commissions"),
        choice("grants_subsidies", "Grants/subsidies"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "text",
      name: "revenue_last_year",
      inputType: "number",
      validators: [regexValidators.non_negative_integer()],
      title: {
        en: "What was the cooperative's estimated revenue last year (in RWF)?",
        rw: "Ni amafaranga angahe yinjiye ishyirahamwe mu mwaka ushize (muri RWF)?",
      },
    },
    {
      type: "text",
      name: "expenses_last_year",
      inputType: "number",
      validators: [regexValidators.non_negative_integer()],
      title: {
        en: "What were the cooperative's estimated expenses last year (in RWF)?",
        rw: "Ni amafaranga angahe yakoreshejwe n'ishyirahamwe mu mwaka ushize (muri RWF)?",
      },
    },
    {
      type: "text",
      name: "revenue_two_years_ago",
      inputType: "number",
      validators: [regexValidators.non_negative_integer()],
      title: {
        en: "What was the cooperative's estimated revenue two years ago (in RWF)?",
        rw: "Ni amafaranga angahe yinjiye ishyirahamwe mu myaka ibiri ishize (muri RWF)?",
      },
    },
    {
      type: "text",
      name: "expenses_two_years_ago",
      inputType: "number",
      validators: [regexValidators.non_negative_integer()],
      title: {
        en: "What were the cooperative's estimated expenses two years ago (in RWF)?",
        rw: "Ni amafaranga angahe yakoreshejwe n'ishyirahamwe mu myaka ibiri ishize (muri RWF)?",
      },
    },
    {
      type: "checkbox",
      name: "loan_usage",
      title: {
        en: "What are the intended uses of the members' loans?",
        rw: "Ni iki inguzanyo z'abanyamuryango zigamije gukoreshwa?",
      },
      choices: [
        choice("seeds", "Seeds"),
        choice("fertilizers", "Fertilizers"),
        choice("crop_protection", "Crop protection products"),
        choice("labor", "Labor"),
        choice("land_preparation", "Land preparation"),
        choice("land_expansion", "Land expansion / leasing additional land"),
        choice("irrigation_equipment", "Irrigation equipment"),
        choice(
          "post_harvest",
          "Post-harvest infrastructure (drying, storage, processing)",
        ),
        choice("transport_logistics", "Transport and logistics"),
        choice("farm_equipment", "Farm equipment and machinery"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "radiogroup",
      name: "loan_size",
      title: {
        en: "What loan amount do cooperative members consider most suitable for their farming activities?",
        rw: "Ni inguzanyo ihe abanyamuryango batekereza ko ikwiye ku buhinzi bwabo?",
      },
      choices: [
        choice("lt_100k", "< 100K"),
        choice("100k_500k", "100K-500K"),
        choice("500k_1m", "500K-1M"),
        choice("1m_2m", "1-2M"),
        choice("gt_2m", "> 2M"),
      ],
    },
    {
      type: "dropdown",
      name: "guarantor_willingness",
      title: {
        en: "Is the cooperative willing to act as a guarantor for member loans?",
        rw: "Ese ishyirahamwe ryiteguye kuba ingwate y'inguzanyo z'abanyamuryango?",
      },
      choices: [
        choice("no", "No"),
        choice("yes_conditions", "Yes with specific conditions"),
        choice("yes_selected", "Yes, for selected members only"),
        choice("yes_all", "Yes, for all eligible members"),
      ],
    },
    {
      type: "dropdown",
      name: "guarantor_experience",
      title: {
        en: "Has the cooperative previously guaranteed loans for its members?",
        rw: "Ese ishyirahamwe ryigeze kuba ingwate y'inguzanyo z'abanyamuryango?",
      },
      description: {
        en: "Rarely = once or twice in the last 3 years. Regularly = in most seasons.",
        rw: "Gake = rimwe cyangwa kabiri mu myaka 3 ishize. Kenshi = mu bihembwe byinshi.",
      },
      choices: [
        choice("never", "Never", "Nta na rimwe"),
        choice("rarely", "Rarely", "Gake"),
        choice("regularly", "Regularly", "Kenshi"),
      ],
    },
  ],
});

const governanceCompliance = () => ({
  name: "page6",
  title: { en: "Governance & Compliance", rw: "Imiyoborere n'ubwitange" },
  elements: [
    {
      type: "text",
      name: "board_members_count",
      inputType: "number",
      title: {
        en: "How many members does the board of the cooperative have?",
        rw: "Komite nyobozi y'ishyirahamwe igizwe n'abantu bangahe?",
      },
      validators: [
        {
          type: "regex",
          regex: "^([1-9]|1[0-9]|20)$",
          text: {
            en: "Please enter a whole number between 1 and 20",
            rw: "Andika umubare wuzuye uri hagati ya 1 na 20",
          },
        },
      ],
    },
    {
      type: "radiogroup",
      name: "board_changed",
      title: {
        en: "Has the board of the cooperative changed since the previous MoneyPhone assessment (for a new cooperative: in the past 12 months)?",
        rw: "Ese komite nyobozi y'ishyirahamwe yaba yarahindutse kuva ku isuzuma rya MoneyPhone riheruka (ku ishyirahamwe rishya: mu mezi 12 ashize)?",
      },
      description: {
        en: "Any change of board members counts. Re-electing the same people is not a change. If there was more than one change, choose the most serious one.",
        rw: "Guhinduka uko ari ko kose kw'abagize komite kubarwa. Kongera gutora abantu basanzweho ntibibarwa nk'ihinduka. Niba hari impinduka zirenze imwe, hitamo iyikomeye kurusha izindi.",
      },
      choices: [
        choice("no_change", "No change", "Nta cyahindutse"),
        choice(
          "yes_regular_elections",
          "Yes, through regular elections",
          "Yego, binyuze mu matora asanzwe",
        ),
        choice(
          "yes_outside_elections",
          "Yes, outside regular elections (resignation, removal, conflict)",
          "Yego, bidaturutse ku matora asanzwe (kwegura, kwirukanwa, amakimbirane)",
        ),
      ],
    },
    {
      type: "radiogroup",
      name: "president_changed",
      visibleIf:
        "{board_changed} = 'yes_regular_elections' or {board_changed} = 'yes_outside_elections'",
      title: {
        en: "Did the president change?",
        rw: "Ese perezida yarahindutse?",
      },
      choices: [choice("no", "No", "Oya"), choice("yes", "Yes", "Yego")],
    },
    {
      type: "text",
      name: "new_board_members_count",
      inputType: "number",
      min: 0,
      maxValueExpression: "{board_members_count}",
      visibleIf:
        "{board_changed} = 'yes_regular_elections' or {board_changed} = 'yes_outside_elections'",
      title: {
        en: "How many of the current board members are new?",
        rw: "Ni bangahe mu bagize komite y'ubu bashya?",
      },
      validators: [
        regexValidators.non_negative_integer(),
        {
          type: "expression",
          text: {
            en: "Cannot exceed the total number of board members",
            rw: "Ntibishobora kurenga umubare wose w'abagize komite nyobozi",
          },
          expression:
            "{board_members_count} empty or {new_board_members_count} empty or {new_board_members_count} <= {board_members_count}",
        },
      ],
    },
    {
      type: "text",
      name: "board_meetings",
      inputType: "number",
      validators: [regexValidators.non_negative_integer()],
      title: {
        en: "How many board meetings were held in the last 12 months?",
        rw: "Ni inama zingahe z'ikomite y'ubuyobozi zabereye mu mezi 12 ashize?",
      },
    },
    {
      type: "text",
      name: "general_assembly_meetings",
      inputType: "number",
      validators: [regexValidators.non_negative_integer()],
      title: {
        en: "How many general assembly meetings were held in the last 12 months?",
        rw: "Ni inama zingahe z'ishyirahamwe rusange zabereye mu mezi 12 ashize?",
      },
    },
    {
      type: "text",
      name: "audit_committee_meetings",
      inputType: "number",
      validators: [regexValidators.non_negative_integer()],
      title: {
        en: "How many audit committee meetings were held in the last 12 months?",
        rw: "Ni inama zingahe z'ikomite isuzuma zabereye mu mezi 12 ashize?",
      },
    },
    {
      type: "text",
      name: "general_assembly_attendees",
      inputType: "number",
      title: {
        en: "How many members attended the last general assembly?",
        rw: "Ni abanyamuryango bangahe bitabiriye inama y'inteko rusange iheruka?",
      },
      validators: [
        regexValidators.non_negative_integer(),
        {
          type: "expression",
          text: {
            en: "Number of attendees cannot exceed the total number of members",
            rw: "Umubare w'abitabiriye ntushobora kurenga umubare wose w'abanyamuryango",
          },
          expression:
            "{members} empty or {general_assembly_attendees} empty or {general_assembly_attendees} <= {members}",
        },
      ],
    },
    {
      type: "expression",
      name: "attendance_rate_general_assembly",
      title: {
        en: "Average attendance rate — general assembly (%) (calculated automatically)",
        rw: "Ijanisha ry'abitabiriye inteko rusange (%) (byikora)",
      },
      expression: "calcAttendanceRate({general_assembly_attendees}, {members})",
      includeIntoResult: true,
    },
    {
      type: "checkbox",
      name: "action_member_default",
      title: {
        en: "What does the cooperative do when a member does not repay?",
        rw: "Ni iki ishyirahamwe rikora iyo umunyamuryango atishyuye?",
      },
      hasNone: true,
      noneText: {
        en: "No fixed approach",
        rw: "Nta buryo buhamye buhari",
      },
      choices: [
        choice(
          "committee_visit",
          "A committee visits the member to understand the problem",
          "Komite isura umunyamuryango ngo isobanukirwe ikibazo",
        ),
        choice(
          "grace_period_restructure",
          "Agrees on an extended grace period or repayment schedule",
          "Bumvikana ku gihe cy'inyongera cyangwa gahunda nshya yo kwishyura",
        ),
        choice(
          "guarantor_deduction",
          "Deducts from the guarantor's produce or savings",
          "Bakata ku musaruro cyangwa ubwizigame bw'ingwate",
        ),
        choice(
          "withhold_services",
          "Withholds cooperative services or produce payment",
          "Bafata serivisi z'ishyirahamwe cyangwa amafaranga y'umusaruro",
        ),
        choice(
          "legal_local_authorities",
          "Involves local authorities or legal action",
          "Bitabaza ubuyobozi bw'ibanze cyangwa inzego z'ubutabera",
        ),
      ],
      validators: [
        {
          type: "expression",
          text: {
            en: "'No fixed approach' cannot be selected together with other options",
            rw: "'Nta buryo buhamye buhari' ntibishobora gutoranywa hamwe n'ibindi",
          },
          expression:
            "{action_member_default} empty or {action_member_default} notcontains 'none' or count({action_member_default}) = 1",
        },
      ],
    },
  ],
});

const climateInsuranceIrrigation = () => ({
  name: "page7",
  title: {
    en: "Climate, Insurance and Irrigation",
    rw: "Ibihe, ubwishingizi n'ubuhinzi bw'ubwoba",
  },
  elements: [
    {
      type: "boolean",
      name: "climate_events",
      title: {
        en: "Has the cooperative area experienced any significant weather-related events during the past 2 years?",
        rw: "Ese akarere k'ishyirahamwe kigeze kubona ibihe bidasanzwe mu myaka 2 ishize?",
      },
      labelTrue: { en: "Yes", rw: "Yego" },
      labelFalse: { en: "No", rw: "Oya" },
    },
    {
      type: "dropdown",
      name: "climate_impact",
      visibleIf: "{climate_events} = true",
      title: {
        en: "What was the impact of weather related events on production during the past 2 years?",
        rw: "Ni irihe tandukaniro ry'ibihe bidasanzwe byagize ku musaruro mu myaka 2 ishize?",
      },
      choices: [
        choice("no_impact", "No impact"),
        choice("minor", "Minor, small reduction in production"),
        choice("moderate", "Moderate, noticeable reduction in production"),
        choice(
          "significant",
          "Significant, major reduction affecting most farmers",
        ),
        choice("severe", "Severe, crop failure or major losses"),
      ],
    },
    {
      type: "text",
      name: "members_with_irrigation",
      inputType: "number",
      title: {
        en: "How many cooperative members have access to irrigation systems?",
        rw: "Ni abanyamuryango bangahe bafite uburyo bwo gukoresha uburyo bwo kuhira?",
      },
      validators: [
        regexValidators.non_negative_integer(),
        expressions.max_members("members_with_irrigation"),
      ],
    },
    {
      type: "text",
      name: "members_with_crop_insurance",
      inputType: "number",
      title: {
        en: "How many cooperative members currently have crop insurance?",
        rw: "Ni abanyamuryango bangahe bafite ubwishingizi bw'ibihingwa?",
      },
      validators: [
        regexValidators.non_negative_integer(),
        expressions.max_members("members_with_crop_insurance"),
      ],
    },
  ],
});

const financialAccess = () => ({
  name: "page8",
  title: { en: "Financial Access", rw: "Kugera ku mafaranga" },
  elements: [
    {
      type: "text",
      name: "access_to_credit",
      inputType: "number",
      title: {
        en: "How many cooperative members currently have access to formal credit?",
        rw: "Ni abanyamuryango bangahe bafite uburyo bwo kubona inguzanyo zemewe?",
      },
      validators: [
        regexValidators.non_negative_integer(),
        expressions.max_members("access_to_credit"),
      ],
    },
    {
      type: "checkbox",
      name: "credit_sources",
      title: {
        en: "What are the main sources of formal credit available to cooperative members?",
        rw: "Ni ayahe maturutse y'inguzanyo zemewe abanyamuryango bashobora kubona?",
      },
      choices: [
        choice("saccos", "SACCOs"),
        choice("banks", "Banks"),
        choice("mfis", "MFIs"),
        choice("savings_groups", "Savings groups"),
        choice("input_suppliers", "Input suppliers"),
        choice(
          "buyers_contract_farming",
          "Buyers / Contract Farming Arrangements",
        ),
        choice("government_programs", "Government programs"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
    {
      type: "text",
      name: "interest_rate",
      inputType: "number",
      title: {
        en: "What is the average annual interest rate charged by the financial institution most commonly used by cooperative members?",
        rw: "Ni irihe percent ry'inyungu ku mwaka ryishyurwa n'ikigo cy'imari abanyamuryango bakoresha cyane?",
      },
      validators: [
        {
          type: "numeric",
          minValue: 0,
          maxValue: 100,
          text: {
            en: "Please enter an interest rate between 0 and 100%",
            rw: "Andika ijanisha ry'inyungu riri hagati ya 0 na 100%",
          },
        },
      ],
    },
    {
      type: "text",
      name: "turnaround_time",
      inputType: "number",
      title: {
        en: "What is the average loan turnaround time (from application to disbursement) of that financial institution?",
        rw: "Ni iminsi ingahe hagati yo gusaba no kubona inguzanyo muri icyo kigo cy'imari?",
      },
      validators: [
        {
          type: "regex",
          regex: "^([0-9]|[1-9][0-9]|[12][0-9]{2}|3[0-5][0-9]|36[0-5])$",
          text: {
            en: "Please enter a whole number of days between 0 and 365",
            rw: "Andika umubare wuzuye w'iminsi uri hagati ya 0 na 365",
          },
        },
      ],
    },
    {
      type: "checkbox",
      name: "challenges_loan_access",
      isRequired: true,
      requiredErrorText: {
        en: "Please select at least one challenge",
        rw: "Nyamuneka hitamo nibura imbogamizi imwe",
      },
      title: {
        en: "What are the main challenges cooperative members face when accessing loans?",
        rw: "Ni ibihe bibazo bikuru abanyamuryango bahura nabyo mu kubona inguzanyo?",
      },
      choices: [
        choice("high_interest", "High interest rates"),
        choice("collateral", "Collateral requirements"),
        choice("long_turnaround", "Long turnaround time"),
        choice("small_amounts", "Small loan amounts"),
        choice("complex_procedures", "Complex procedures"),
        choice("short_repayment", "Short repayment periods"),
      ],
      hasOther: true,
      otherText: { en: "Other", rw: "Ibindi" },
      storeOthersAsComment: false,
    },
  ],
});

const supportingDocuments = () => ({
  name: "page9",
  title: { en: "Supporting Documents", rw: "Inyandiko zishyigikira" },
  elements: [
    {
      type: "file_uploader",
      name: "rca_certificate",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload a copy of the Legal Registration Document",
        rw: "Shyiraho kopi y'inyandiko y'iyandikisha",
      },
    },
    {
      type: "file_uploader",
      name: "members_list",
      isRequired: true,
      requiredErrorText: {
        en: "Please upload all required documents before submitting.",
        rw: "Nyamuneka shyiraho inyandiko zose zisabwa mbere yo kohereza.",
      },
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the latest version of the list of membership *",
        rw: "Shyiraho urutonde rw'ubu rw'abanyamuryango *",
      },
    },
    {
      type: "file_uploader",
      name: "financial_reports",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the latest financial reports",
        rw: "Shyiraho raporo y'imari iheruka",
      },
    },
    {
      type: "file_uploader",
      name: "contract",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload contracts with buyers if any",
        rw: "Shyiraho amasezerano n'abaguzi niba ahari",
      },
    },
    {
      type: "file_uploader",
      name: "business_records",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload business records (production, sales, transaction records, balance sheets, etc. if available)",
        rw: "Shyiraho inyandiko z'ubucuruzi (umusaruro, ubucuruzi, inyandiko z'ubucuruzi, balance sheets, n'ibindi niba bihari)",
      },
    },
    {
      type: "file_uploader",
      name: "assembly_attendance",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the list of attendance of the latest general assembly meeting",
        rw: "Shyiraho urutonde rw'abitabiriye inama y'ishyirahamwe rusange iheruka",
      },
    },
    {
      type: "file_uploader",
      name: "general_assembly_minutes",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the latest meeting minutes of the general assembly meetings",
        rw: "Shyiraho inyandiko z'inama y'ishyirahamwe rusange iheruka",
      },
    },
    {
      type: "file_uploader",
      name: "audit_committee_minutes",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the latest meeting minutes and reports of the auditing committee meetings",
        rw: "Shyiraho inyandiko n'raporo z'ikomite isuzuma iheruka",
      },
    },
    {
      type: "file_uploader",
      name: "relevant_documents",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload procedures, manuals or other relevant documents",
        rw: "Shyiraho amabwiriza, inyandiko cyangwa izindi nyandiko z'ingirakamaro",
      },
    },
    {
      type: "file_uploader",
      name: "insurance_contract",
      accept: "image/*,application/pdf",
      title: {
        en: "Upload the contract with the insurance company, if any",
        rw: "Shyiraho amasezerano n'ikigo cy'ubwishingizi niba ahari",
      },
    },
  ],
});

export const cooperative_assessment_rwanda_direct_model = (
  clientUrlForCatalog,
) => ({
  showProgressBar: "top",
  showQuestionNumbers: "off",
  sendResultOnPageNext: true,
  firstPageIsStarted: false,
  showCompletedPage: false,
  checkErrorsMode: "onValueChanged",
  completeText: { en: "Submit assessment", rw: "Ohereza isuzuma" },
  pageNextText: translate("general.pageNextText"),
  pagePrevText: translate("general.pagePrevText"),
  requiredText: "",
  locale: "en",
  triggers: [
    {
      type: "setvalue",
      expression: "{sales_records} = 'no_records'",
      setToName: "record_keeping",
      setValue: "no_records",
    },
    {
      type: "setvalue",
      expression: "{sales_records} = 'no_records'",
      setToName: "data_verification",
      setValue: "no_records",
    },
  ],
  pages: [
    generalInformation(clientUrlForCatalog),
    productionValueChain(),
    marketAccess(),
    operationalMaturity(),
    financialPerformance(),
    governanceCompliance(),
    climateInsuranceIrrigation(),
    financialAccess(),
    supportingDocuments(),
  ],
});
